/**
 * Keš HTML webu (řez 11, vize F10.2) — sdílené konstanty a JEDINÁ normalizace parametrů.
 *
 * Web ukládá vykreslené HTML do Cache API v middleware a jednu uloženou odpověď
 * servíruje všem návštěvníkům se stejným klíčem. Klíč proto smí nést jen parametry
 * adresy, podle kterých server HTML doopravdy mění: parametr, který server čte a klíč
 * ho nemá, by jednomu návštěvníkovi naservíroval stránku vykreslenou pro jiného;
 * parametr v klíči, který server nečte (`utm_*`, `gclid`, výběr termínu na detailu
 * zájezdu), by jen tříštil keš na kopie téže stránky.
 *
 * Tabulky jsou dvě a druhá vzniká z první (řez 02 vize podnety-tymu-2026-10, F5.2):
 *
 * - `SERVER_READ_PARAMS` je inventura čtení serverem po cestách VNITŘNÍMI (anglickými)
 *   názvy: loadery a šablony čtou adresu za `serverUrl`, která je už přeložená dovnitř.
 *   Páruje ji měřidlo `scripts/kontrola/kes-html.mjs parametry` (server nečte nic mimo ni).
 * - `SERVER_QUERY_PARAMS` je táž tabulka přeložená slovníkem (`webParams.ts`) na WEBOVÉ
 *   (české) názvy: z nich se skládá klíč keše, protože adresa stránky je česky. Anglický
 *   název tak v klíči nikdy není a stará anglická adresa dostane kopii holé stránky.
 *   Ručně psaná druhá tabulka by se s inventurou časem rozešla.
 *
 * `serverSearchParams` je jediné místo, které z adresy vybírá a řadí parametry (webové
 * názvy) — volá ho klíč keše v middleware i `serverInternalSearchParams`, jediné místo,
 * kde se vybraná adresa překládá dovnitř (`serverUrl` ve webu). Dvě normalizace by se časem
 * rozešly a klíč by přestal odpovídat tomu, co server vykreslil.
 *
 * Směr importů: modul importuje `webParams.ts`, ten tenhle modul (ani `tripWizard.ts`)
 * importovat nesmí — tabulky se počítají při načtení modulu a ESM cyklus by je vydal
 * `undefined` (TDZ) při startu middleware i měřidla.
 *
 * Modul je serverový (middleware webu, API): nemá přímý import zod, ale konstanty filtrů
 * bere z `tripFilters.ts`/`tripWizard.ts`, které zod táhnou, takže ho klientský skript
 * (`<script>` v `.astro`) neimportuje.
 */

import {
  INSPIRATION_TRIPS_EXPAND_PARAM,
  TRIP_DETAIL_CLIENT_ONLY_PARAMS,
  routePrefixes,
  routes,
} from "./routes";
import { TRIP_FILTER_PARAM_KEYS } from "./tripFilters";
import { WIZARD_STEP_PARAM, WIZARD_STEPS } from "./tripWizard";
import {
  pathEntryFor,
  webParamName,
  webParamsToInternal,
  webPathname,
  type WebPathEntry,
} from "./webParams";

/** Stav keše na HTML odpovědi, kterou middleware posoudil (`HtmlCacheStatus`). */
export const HTML_CACHE_STATUS_HEADER = "X-Html-Cache";
/** Čas uložení položky (ISO 8601) na `HIT`, `STALE` a na právě uloženém `MISS`. */
export const HTML_CACHE_STORED_HEADER = "X-Html-Cache-Stored";
/**
 * Značka generace, kterou posouzení přečetlo (ms od epochy), nebo `none`, když čtení
 * vrátilo `null` (výpadek, limit) — na `HIT`, `STALE` i `MISS`, NIKDY na uložené položce.
 * Diagnostika promazání zvenku: stará kopie s `none` = keš rozhodla jen podle stáří.
 */
export const HTML_CACHE_GENERATION_HEADER = "X-Html-Cache-Generation";
/** Hodnota `HTML_CACHE_GENERATION_HEADER`, když značka nebyla k dispozici. */
export const HTML_CACHE_GENERATION_NONE = "none";
/** Verze buildu — jen na uložené položce, NIKDY v klíči (rozbila by promazání po adrese). */
export const HTML_CACHE_VERSION_HEADER = "X-Html-Cache-Version";
/** Štítek (`Cache-Tag`) každé uložené položky; promazání „všech stránek" jde přes něj. */
export const HTML_CACHE_TAG_ALL = "ckgo2-html";
/** Parametr, který keš obchází (čtení i zápis) a nikdy není v klíči. */
export const HTML_CACHE_BYPASS_PARAM = "nc";
/**
 * Držení položky v Cache API, v sekundách (7 dní; dřív 24 h). Změny z adminu platnost
 * neohrožují (značka generace), nové nasazení položky zneplatní verzí buildu, takže delší
 * držení znamená jen to, že na render nečeká ani první návštěva stránky po pauze.
 * Strop dává `HTML_CACHE_GENERATION_TTL_S`: vypršelá značka by položky zneplatnila stejně.
 */
export const HTML_CACHE_RETENTION_S = 7 * 86_400;
/**
 * Okno obnovy na pozadí od uložení, v sekundách = celé držení položky. Po čerstvosti
 * dostane návštěvník VŽDY uloženou kopii hned a stránka se přerenderuje na pozadí;
 * synchronní render zbývá jen bez položky (první návštěva v colo, po nasazení, po 7 dnech) a po značce
 * generace (uložení v adminu, `htmlCacheGenerationUrl`), takže změny z adminu platí dál
 * do minuty.
 *
 * Dřív 600 s (výklad F10.2 „změny mimo admin do 15 minut“, spor 1 vize web-podzim-2026):
 * při malém provozu tak skoro každá návštěva po pauze čekala na celý render (homepage
 * 17–20 s). Majitel 5. 10. 2026 rozhodl, že rychlost má přednost: obsazenost po objednávce
 * nebo nová recenze se prvnímu návštěvníkovi po delší pauze ukáže o jednu obnovu později.
 */
export const HTML_CACHE_SWR_WINDOW_S = HTML_CACHE_RETENTION_S;

/**
 * Značka generace keše HTML (oprava řezu 11 po kroku 3f nasazení).
 *
 * Promazání zóny (`purge_cache` po adrese, štítkem i hostem) položky, které funkce
 * Pages uložila přes Cache API, NEMAŽE: Cache API funkce Pages neleží v keši, kterou
 * promazání zóny obsluhuje (doloženo smyčkou v `docs/reviews/rez-11-diagnoza-pokus-3.md`).
 * Položky Cache API Workeru API (`api.` v téže zóně) promazání po adrese maže. Proto
 * platnost položky HTML nese malá značka v keši Workeru API: pro každou cestu a pro
 * „všechny stránky" čas, kdy značka vznikla. Promazání po uložení smaže značky
 * (`htmlCacheGenerationKeys`), API je při dalším čtení založí s novým časem a web při
 * posouzení položky vezme mladší z obou (`htmlCacheGenerationUrl`): položka uložená
 * před značkou je neplatná (synchronní render, při jeho selhání stará kopie, S13).
 *
 * Značka vzniká při prvním čtení, a web ji čte u KAŽDÉHO posouzení, i při `MISS` před
 * renderem: uložená položka je tak vždy mladší než značka, kterou právě založila, a
 * nová značka (po promazání nebo vypadnutí z keše) položky jen zneplatní, nikdy nezachrání.
 */
export const HTML_CACHE_GENERATION_API_PATH = "/api/html-cache/generation";
/** Parametr endpointu značky s cestou stránky (tvar `htmlCachePathname`). */
export const HTML_CACHE_GENERATION_PATH_PARAM = "path";
/**
 * Jak dlouho API drží značku (s): déle než položku (`HTML_CACHE_RETENTION_S`), aby
 * vypršení značky zbytečně nezneplatnilo položky, které ještě žijí.
 */
export const HTML_CACHE_GENERATION_TTL_S = 8 * 86_400;

/**
 * Adresa endpointu značky pro stránku s cestou `pathname` — web ji volá při každém
 * posouzení položky. Cesta se normalizuje stejně jako klíč položky (`htmlCachePathname`),
 * parametry adresy stránky do ní nejdou (značka platí pro všechny varianty cesty).
 */
export function htmlCacheGenerationUrl(apiBase: string, pathname: string): string {
  const url = new URL(HTML_CACHE_GENERATION_API_PATH, apiBase);
  url.searchParams.set(HTML_CACHE_GENERATION_PATH_PARAM, htmlCachePathname(pathname));
  return url.href;
}

/**
 * Klíče značek v keši Workeru API — JEDINÝ tvar pro čtení (endpoint značky) i promazání
 * (`executePurgePlans`): obě strany je musí skládat bajtově stejně, jinak promazání
 * značku tiše mine. `all` = všechny stránky, `page` = jedna cesta.
 *
 * `null`, když cesta není kanonická cesta stránky: klíč se skládá z cesty, kterou posílá
 * kdokoli (parametr veřejného endpointu), a URL parser by tečkové segmenty (`..`, i
 * zakódované), zpětná lomítka nebo `?` přeložil na JINOU adresu — třeba na adresu
 * odpovědi API, kterou by pak edge keš vydávala jako značku. Platí jen cesta, kterou
 * parser nezmění.
 */
export function htmlCacheGenerationKeys(
  apiOrigin: string,
  pathname: string,
): { all: string; page: string } | null {
  if (!pathname.startsWith("/")) return null;
  const pagePath = `${HTML_CACHE_GENERATION_API_PATH}/page${htmlCachePathname(pathname)}`;
  let page: URL;
  try {
    page = new URL(pagePath, apiOrigin);
  } catch {
    return null;
  }
  if (page.pathname !== pagePath || page.search !== "" || page.hash !== "") return null;
  return {
    all: new URL(`${HTML_CACHE_GENERATION_API_PATH}/all`, apiOrigin).href,
    page: page.href,
  };
}

export type HtmlCacheStatus = "HIT" | "STALE" | "MISS" | "BYPASS";

/**
 * Parametry, které server čte na jedné cestě. `path` bez koncového lomítka; `exact` =
 * shoda celé cesty, `prefix` = cesta sama nebo cokoli pod ní po hranici segmentu,
 * `below` = jen cesty pod ní (cesta sama ne). `below` dovoluje dát rozbočovači (`exact`)
 * a stránkám pod ním (`below`) různé parametry bez spoléhání na pořadí položek: obě
 * položky smějí mít totéž `path`, žádnou cestu ale nezasáhnou obě.
 * `reason` jmenuje funkci nebo soubor, který parametry čte — kdo tabulku mění, musí
 * vědět, proč tam položka je.
 */
export interface ServerQueryParamsEntry extends WebPathEntry {
  params: readonly string[];
  reason: string;
}

/**
 * Cesta, pod kterou keš HTML stránku vede: bez koncového lomítka, kořen `/` zůstává.
 * Je to normalizace slovníku (`webPathname`), kterou volá i výběr položky
 * (`pathEntryFor`): klíč keše ve webu (`htmlCacheKey`), výběr parametrů a plocha slovníku
 * tak cestu normalizují jedinou funkcí. Dvě kopie by se časem rozešly a klíč by nesl
 * parametry jiné položky tabulky, než k jaké patří jeho cesta.
 */
export function htmlCachePathname(pathname: string): string {
  return webPathname(pathname);
}

/** Sjednocení seznamů bez duplicit, v pořadí prvního výskytu. */
const paramList = (...lists: ReadonlyArray<readonly string[]>): readonly string[] => [
  ...new Set(lists.flat()),
];

/**
 * Průvodce čte jen klíče svých kroků a krok (`readWizardState` bere `tripType`,
 * `duration[0]`, `bestMonth` a `krok`); ostatní filtry nevykresluje, v klíči by jen
 * tříštily keš.
 */
const WIZARD_PARAMS = paramList(
  WIZARD_STEPS.map((step) => step.key),
  [WIZARD_STEP_PARAM],
);

/**
 * Inventura čtení parametrů serverem webu vnitřními názvy (stav k 17. 10. 2026). Klientská
 * čtení (`topic` na kontaktu, `paid` v portálu, `t` v centru preferencí, skripty a
 * `window.location`) sem nepatří; osobní token nikdy nesmí do klíče.
 */
export const SERVER_READ_PARAMS: readonly ServerQueryParamsEntry[] = [
  {
    path: routes.blog(),
    match: "exact",
    params: ["category", "page"],
    reason: "pages/blog.astro čte category a page",
  },
  {
    path: routes.recenze(),
    match: "prefix",
    params: ["destinace", "klienti", "google"],
    reason:
      "pages/recenze/index.astro čte destinace (301 na fasetu), klienti a google (strany " +
      "sloupců); pages/recenze/[destinace].astro čte klienti a google (řez 03; zaniklý page " +
      "nečte nic, F5.2)",
  },
  {
    path: routes.recenzeKarty(),
    match: "exact",
    params: ["zdroj", "destinace", "strana"],
    reason: "pages/recenze-karty.astro čte zdroj, destinace a strana (fragment karet, řez 03)",
  },
  {
    path: routes.kdyVyrazit(),
    match: "exact",
    params: ["mesic"],
    reason: "pages/kdy-vyrazit.astro čte mesic",
  },
  {
    path: routes.inspirace(),
    match: "exact",
    params: ["type", "destination", "page"],
    reason: "loadInspirationsListing (lib/inspirationsListing.ts)",
  },
  {
    // Detail inspirace (`/inspirace/<zeme>/<slug>` i plochý `/inspirace/<slug>`); hub
    // `/inspirace` sám má vlastní položku výše a tahle ho nezasáhne (`below`).
    path: routes.inspirace(),
    match: "below",
    params: [INSPIRATION_TRIPS_EXPAND_PARAM],
    reason:
      "components/pages/InspirationDetail.astro čte zajezdy (rozbalení skupin sekce zájezdů, řez 06)",
  },
  {
    path: routes.inspiraceZajezdyKarty(),
    match: "exact",
    params: ["inspirace", "skupina"],
    reason:
      "pages/inspirace-zajezdy.astro čte inspirace a skupina (fragment karet, řez 06; sám v keši " +
      "HTML není, vydává Cache-Control: no-store)",
  },
  {
    path: routes.zajezdy(),
    match: "prefix",
    params: paramList(["view", "sort", "page"], TRIP_FILTER_PARAM_KEYS),
    reason: "loadTripsListing (lib/tripsListing.ts) a parseFilterSearchParams (tripFilters.ts)",
  },
  {
    path: routes.kviz(),
    match: "exact",
    params: WIZARD_PARAMS,
    reason: "readWizardState (packages/shared/src/tripWizard.ts): jen klíče kroků a krok",
  },
  {
    path: routes.hledacInspiraci(),
    match: "exact",
    params: WIZARD_PARAMS,
    reason: "readWizardState (packages/shared/src/tripWizard.ts): jen klíče kroků a krok",
  },
  {
    path: routes.zajezdNaMiru(),
    match: "exact",
    params: ["destination", "dateFrom", "dateTo", "tripType", "from"],
    reason: "parseCustomInquiryContext (customInquiry.ts) přes loadCustomInquiryPage",
  },
  {
    path: routes.hotely(),
    match: "exact",
    params: ["destination"],
    reason: "parseFilterSearchParams(…).destination v pages/hotely/index.astro",
  },
  {
    path: routes.kontakt(),
    match: "exact",
    params: ["topic"],
    reason: "pages/kontakt.astro čte topic (předvybrané téma formuláře, F5.2)",
  },
  {
    path: htmlCachePathname(routePrefixes.zajezd),
    match: "prefix",
    params: [],
    reason:
      "detail zájezdu parametry nečte: TRIP_DETAIL_CLIENT_ONLY_PARAMS " +
      `(${TRIP_DETAIL_CLIENT_ONLY_PARAMS.join(", ")}) čte jen skript v prohlížeči ` +
      "(lib/departureSelection.ts, routes.ts)",
  },
];

/** Vzorová cesta položky: `below` platí jen pod svou cestou. */
const entryProbe = (entry: ServerQueryParamsEntry): string =>
  entry.match === "below" ? `${entry.path}/vzor` : entry.path;

/**
 * Položka inventury přeložená na webové názvy plochy své cesty. Plochu má podle slovníku
 * každá položka (identitní se přeloží samy na sebe); název bez plochy zůstává, ale test
 * tabulky vyžaduje, aby na cestě s plochou byl každý název webový.
 */
const toWebEntry = (entry: ServerQueryParamsEntry): ServerQueryParamsEntry => {
  const probe = entryProbe(entry);
  return { ...entry, params: entry.params.map((name) => webParamName(probe, name)) };
};

/** Klíč keše: `SERVER_READ_PARAMS` webovými názvy slovníku (F5.2). */
export const SERVER_QUERY_PARAMS: readonly ServerQueryParamsEntry[] =
  SERVER_READ_PARAMS.map(toWebEntry);

/**
 * Parametry položky tabulky pro cestu (výběr `pathEntryFor`, týž jako plocha slovníku);
 * bez shody `[]`. Obcházecí `nc` ve výsledku nikdy není.
 */
function paramsFor(table: readonly ServerQueryParamsEntry[], pathname: string): readonly string[] {
  const best = pathEntryFor(table, pathname);
  return best ? best.params.filter((name) => name !== HTML_CACHE_BYPASS_PARAM) : [];
}

/** Parametry, které server na cestě čte, vnitřními názvy (inventura, měřidlo `kes-html`). */
export function serverReadParamsFor(pathname: string): readonly string[] {
  return paramsFor(SERVER_READ_PARAMS, pathname);
}

/** Parametry klíče keše na cestě, webovými názvy (adresa stránky). */
export function serverQueryParamsFor(pathname: string): readonly string[] {
  return paramsFor(SERVER_QUERY_PARAMS, pathname);
}

/**
 * Parametry adresy, podle kterých server na její cestě vykresluje — nové `URLSearchParams`
 * jen s deklarovanými WEBOVÝMI páry (všechny opakované hodnoty), bez `nc`, seřazené podle
 * jména a pak hodnoty porovnáním kódových jednotek (ne `localeCompare`: pořadí nesmí záviset
 * na prostředí). Jediná normalizace parametrů v řezu — volající neřadí sami.
 */
export function serverSearchParams(url: URL): URLSearchParams {
  const allowed = new Set(serverQueryParamsFor(url.pathname));
  const pairs: Array<[string, string]> = [];
  for (const [name, value] of url.searchParams) {
    if (allowed.has(name)) pairs.push([name, value]);
  }
  pairs.sort(([aName, aValue], [bName, bValue]) =>
    aName !== bName ? (aName < bName ? -1 : 1) : aValue < bValue ? -1 : aValue > bValue ? 1 : 0,
  );
  return new URLSearchParams(pairs);
}

/**
 * Totéž přeložené dovnitř (`webParamsToInternal`): adresa, kterou server čte vnitřními
 * názvy (`serverUrl` ve webu). JEDINÉ místo překladu vybrané adresy — nad výsledkem už
 * nikdo nepřekládá (dvojí překlad by zahodil každý vnitřní název odlišný od webového).
 */
export function serverInternalSearchParams(url: URL): URLSearchParams {
  return webParamsToInternal(url.pathname, serverSearchParams(url));
}
